import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react'
import { Button, Input, message, Drawer, Tabs, Modal, Spin } from 'antd'
import './VideoGeneration.scss'

import StepTitle from '../StepTitle'
import { SettingsContent, AdvancedSettingsContent } from './components/Settings'
import Scene from './components/Scene'
import {
  changeMachineState,
  generateVideo,
  concatenateVideos,
  generateVideoRunway,
  generateTransition,
  getTaskStatus
} from '../../../../services/api/aiVideoGeneration'
import ReactPlayer from 'react-player'
import AIVideoModal from './components/FinalVideo'

import { scenes as defaultScenes } from './helper/data.video'

const VideoGeneration = forwardRef((props, ref) => {
  const { formData, setFormData } = props
  const [scenes, setScenes] = useState([])
  const [selectedSettings, setSelectedSettings] = useState(null)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [transitions, setTransitions] = useState({})
  const [advancedSettingsVisible, setAdvancedSettingsVisible] = useState(false)
  const [advancedSettings, setAdvancedSettings] = useState(null)
  const [videoGenerating, setVideoGenerating] = useState([])
  const [
    generatingVideoForAllScenes,
    setGeneratingVideoForAllScenes
  ] = useState(false)
  const [generateMainVideo, setGenerateMainVideo] = useState(false)

  const [isFirstSceneUpdate, setIsFirstSceneUpdate] = useState(true)
  const [showMainVideo, setShowMainVideo] = useState(false)
  const [finalVideoError, setFinalVideoError] = useState(null)

  useEffect(() => {
    if (formData && formData.shotList && formData.shotList.length > 0) {
      setScenes(formData.shotList)

      if (
        formData.shotList.some(scene => scene.shots.some(shot => !shot.vidSrc))
      ) {
        handleGenerateFullVideo(formData.shotList)
      }
    }
    // setScenes(defaultScenes)
    // handleGenerateFullVideo(defaultScenes)
  }, [])

  useEffect(() => {
    if (isFirstSceneUpdate) {
      setIsFirstSceneUpdate(false)
      return
    }

    if (scenes.length > 0) {
      setFormData(prev => ({ ...prev, shotList: scenes }))
    }
  }, [scenes])

  useImperativeHandle(ref, () => ({
    generateFinalVideo: () => {
      handleGenerateFinalVideo()
    }
  }))

  const handleRegenerateVideo = async (sceneId, shotId) => {
    console.log('Regenerating video for sceneId:', sceneId, 'shotId:', shotId)
    try {
      console.log('Regenerating video for sceneId:', sceneId, 'shotId:', shotId)
      setVideoGenerating(prevStatus => [
        ...(prevStatus || []),
        { sceneId, shotId }
      ])

      const scene = scenes.find(s => s.id === sceneId)
      const shot = scene.shots && scene.shots.find(sh => sh.id === shotId)
      // const revisedPrompt = generateRevisedPrompt(sceneId, shotId, scenes)
      // console.log('Revised Prompt:', revisedPrompt)

      const videoData = {
        prompt: shot.shot_direction,
        image_url: shot.imageSrc
      }

      // Call the video generation API
      const response = await generateVideoRunway(videoData)
      const taskId = response && response.data && response.data.task_id

      const videoLink = await waitForVideoTaskSuccess(taskId)

      // Update the scene with the new video link
      setScenes(prevScenes =>
        prevScenes.map(scene => {
          if (scene.id === sceneId) {
            return {
              ...scene,
              shots: scene.shots.map(shot =>
                shot.id === shotId
                  ? {
                      ...shot,
                      ...(videoLink
                        ? { vidSrc: videoLink, errorGeneratingVideo: false }
                        : { errorGeneratingVideo: true })
                    }
                  : shot
              )
            }
          }
          return scene
        })
      )

      if (videoLink) {
        message.success(
          `Video generated for Scene ${sceneId}, Shot ${shotId} successfully!`
        )
      } else {
        throw new Error('Video generation failed')
      }
    } catch (error) {
      message.error('Error regenerating video: ' + error.message)
      console.error('Error regenerating video:', error)
    } finally {
      // Fix: Handle undefined/null prevStatus in filter as well
      setVideoGenerating(prevStatus =>
        (prevStatus || []).filter(
          status => !(status.sceneId === sceneId && status.shotId === shotId)
        )
      )
    }
  }

  async function waitForVideoTaskSuccess(taskId) {
    if (!taskId) {
      throw new Error('Missing task id')
    }

    const maxAttempts = 60
    const delayMs = 10000

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const statusResponse = await getTaskStatus(taskId)
        const data = statusResponse && statusResponse.data ? statusResponse.data : null
        const status = data && data.status ? data.status : null

        if (status === 'success') {
          return data && data.output ? data.output : ''
        }

        if (status === 'failed' || status === 'error') {
          const err = new Error((data && data.error) || 'Task failed')
          err._isTerminal = true
          throw err
        }
      } catch (err) {
        if (err && (err._isTerminal === true || err.message === 'Task failed')) {
          throw err
        }
        if (attempt === maxAttempts - 1) {
          throw err
        }
      }

      await new Promise(resolve => setTimeout(resolve, delayMs))
    }

    throw new Error('Task polling timed out')
  }

  function generateRevisedPrompt(sceneId, shotId, allScenes) {
    // Ensure cleanText only processes strings
    function cleanText(text) {
      if (typeof text !== 'string') return ''
      return text
        .replace(/"/g, '')
        .replace(/'/g, '')
        .replace(/\n/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    }

    // Use strict equality and trim whitespace
    const scene = allScenes.find(scene => scene.id.trim() === sceneId.trim())
    if (!scene) {
      console.warn(`Scene with id "${sceneId}" not found`)
      console.warn(
        'Available scene IDs:',
        allScenes.map(s => `"${s.id}"`)
      )
      return null
    }

    const shot = scene.shots.find(shot => shot.id.trim() === shotId.trim())
    if (!shot) {
      console.warn(`Shot with id "${shotId}" not found in scene "${sceneId}"`)
      console.warn(
        'Available shot IDs:',
        scene.shots.map(s => `"${s.id}"`)
      )
      return null
    }

    console.log('Found shot:', shot)

    try {
      // Start building the revised prompt
      let revisedPrompt = `${cleanText(shot.shot_direction)}. `
      console.log('After shot_direction:', revisedPrompt)

      // Add scene context
      revisedPrompt += `Part of scene ${cleanText(scene.title)} (${cleanText(
        scene.description
      )}). `
      console.log('After scene context:', revisedPrompt)

      // Add shot-specific details
      revisedPrompt += `Shot type: ${cleanText(shot.shotType || 'Unknown')}. `
      revisedPrompt += `Duration: ${shot.clipDuration || 5} seconds. `
      revisedPrompt += `${cleanText(shot.description || '')}`

      console.log('Final revisedPrompt:', revisedPrompt)
      return revisedPrompt
    } catch (err) {
      console.error('Error while generating revisedPrompt:', err)
      return null
    }
  }

  const handleGenerateFinalVideo = async () => {
    try {
      setGenerateMainVideo(true)
      setFinalVideoError(null)

      const payload = convertScenesToVideoArray({
        scenes,
        transitions
      })

      const response = await concatenateVideos({
        payload
      })
      const finalVideo = response && response.data && response.data.output

      setFormData(prev => ({ ...prev, finalVideo }))
    } catch (error) {
      console.error('Error generating final video:', error)
      message.error('Failed to generate final video: ' + error.message)
      setFinalVideoError((error && error.message) || 'Unknown error occurred')
    } finally {
      setGenerateMainVideo(false)
      setShowMainVideo(true)
    }
  }

  function convertScenesToVideoArray(scenesData) {
    const result = []

    // Get all shots in order across all scenes
    const allShots = []
    scenesData.scenes &&
      scenesData.scenes.length > 0 &&
      scenesData.scenes.forEach(scene => {
        scene.shots.forEach(shot => {
          allShots.push({
            ...shot,
            sceneId: scene.id
          })
        })
      })

    // Process each shot
    allShots.forEach((shot, index) => {
      // Add video URL
      if (shot.vidSrc) {
        result.push(shot.vidSrc)
      }

      // Check if there's a transition after this shot
      const transitionKey = `${shot.sceneId}-${shot.id}`
      const transition =
        scenesData.transitions && scenesData.transitions[transitionKey]

      // Only add attributes if there's a transition defined for this specific shot
      if (transition) {
        const attributes = {
          duration: 2.0, // default duration, could be extracted from shot data if available
          transition: transition
        }

        result.push({ attributes })
      }
    })

    return result
  }

  const handleEditSettings = (sceneId, shotId) => {
    const scene = scenes.find(s => s.id === sceneId)
    const shot = scene.shots.find(sh => sh.id === shotId)

    setSelectedSettings({
      sceneId,
      shotId,
      ...scene.settings,
      ...shot,
      motion: shot.motion || 'Static', // Default to 'Static' if motion is not set
      sceneMotion: shot.sceneMotion || 'Natural motion', // Default to 'Natural motion'
      temperature: shot.temperature || 0.7 // Default to 0.7 (balanced) if temperature is not set
    })

    // Debug message to verify this function is called
    console.log('Opening settings drawer for scene:', sceneId, 'shot:', shotId)

    // Set drawer visibility (for Ant Design v3)
    setDrawerVisible(true)
  }

  const handleAdvancedSettings = (sceneId, shotId) => {
    const scene = scenes.find(s => s.id === sceneId)
    const shot = scene.shots.find(sh => sh.id === shotId)

    setAdvancedSettings({
      sceneId,
      shotId,
      characterId:
        shot.characterId ||
        (formData &&
          formData.characterList &&
          formData.characterList[0] &&
          formData.characterList[0].id) ||
        null,
      shotType: shot.shotType || 'Medium Shot',
      imageSrc: shot.imageSrc || null,
      prompt: shot.prompt || '',
      ...shot
    })
    setAdvancedSettingsVisible(true)
  }

  const updateSettings = (key, value) => {
    setSelectedSettings(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const updateAdvancedSettings = (key, value) => {
    setAdvancedSettings(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const saveSettings = () => {
    setScenes(prevScenes =>
      prevScenes.map(scene => {
        if (scene.id === selectedSettings.sceneId) {
          return {
            ...scene,
            shots: scene.shots.map(shot =>
              shot.id === selectedSettings.shotId
                ? {
                    ...shot,
                    shot_direction: selectedSettings.shot_direction,
                    shotType: selectedSettings.shotType,
                    clipDuration: selectedSettings.clipDuration,
                    motion: selectedSettings.motion,
                    sceneMotion: selectedSettings.sceneMotion,
                    temperature: selectedSettings.temperature
                  }
                : shot
            )
          }
        }
        return scene
      })
    )
    setDrawerVisible(false)
  }

  const saveAdvancedSettings = () => {
    setScenes(prevScenes =>
      prevScenes.map(scene => {
        if (scene.id === advancedSettings.sceneId) {
          return {
            ...scene,
            shots: scene.shots.map(shot =>
              shot.id === advancedSettings.shotId
                ? {
                    ...shot,
                    characterId: advancedSettings.characterId,
                    shotType: advancedSettings.shotType,
                    imageSrc: advancedSettings.imageSrc,
                    shot_direction: advancedSettings.shot_direction
                  }
                : shot
            )
          }
        }
        return scene
      })
    )
    setAdvancedSettingsVisible(false)
    message.success('Advanced settings updated successfully!')
  }

  const handleTransitionSelect = (sceneId, position, transitionType) => {
    setTransitions(prev => {
      const newTransitions = { ...prev }
      const key = `${sceneId}-${position}`

      if (transitionType === null) {
        // Actually remove the property from the object
        delete newTransitions[key]
      } else {
        // Set the transition type
        newTransitions[key] = transitionType
      }

      return newTransitions
    })

    if (transitionType === null) {
      message.success('Transition removed')
    } else {
      message.success(`Transition updated to ${transitionType}`)
    }
  }

  const handleUploadVideo = (sceneId, shotId, file) => {
    if (!file) return

    if (!file.type.includes('video/')) {
      message.error('Please upload a video file')
      return
    }

    const videoUrl = URL.createObjectURL(file)

    setScenes(prevScenes =>
      prevScenes.map(scene => {
        if (scene.id === sceneId) {
          return {
            ...scene,
            shots: scene.shots.map(shot =>
              shot.id === shotId ? { ...shot, vidSrc: videoUrl } : shot
            )
          }
        }
        return scene
      })
    )
    message.success('Video uploaded successfully!')
  }

  const handleUploadImage = file => {
    if (!file) return false

    if (!file.type.includes('image/')) {
      message.error('Please upload an image file')
      return false
    }

    const imageUrl = URL.createObjectURL(file)
    updateAdvancedSettings('imageSrc', imageUrl)
    message.success('Image uploaded successfully!')
    return false // Prevent default upload behavior
  }

  const handleGenerateFullVideo = async (data = []) => {
    try {
      setGeneratingVideoForAllScenes(true)

      const allScenes = data.length > 0 ? data : scenes
      console.log('Generating video for all scenes:', allScenes)

      // Create a queue of all shots that need video generation
      const videoQueue = allScenes.flatMap(scene =>
        scene.shots.map(shot => ({ scene, shot }))
      )

      // Process all videos in parallel
      videoQueue.forEach(async ({ scene, shot }) => {
        try {
          const sceneId = scene.id
          const shotId = shot.id

          console.log(
            'Generating video for sceneId:',
            sceneId,
            'shotId:',
            shotId
          )

          // Add to generating status
          setVideoGenerating(prevStatus => [
            ...(prevStatus || []),
            { sceneId, shotId }
          ])

          const revisedPrompt = generateRevisedPrompt(
            sceneId,
            shotId,
            allScenes
          )
          console.log('Revised Prompt:', revisedPrompt)

          const videoData = {
            prompt: shot.shot_direction,
            image_url: shot.imageSrc
          }

          // Call the video generation API
          const response = await generateVideoRunway(videoData)
          const taskId = response && response.data && response.data.task_id
          const videoLink = await waitForVideoTaskSuccess(taskId)

          // Update the scene with the new video link immediately when this request completes
          setScenes(prevScenes =>
            prevScenes.map(scene => {
              if (scene.id === sceneId) {
                return {
                  ...scene,
                  shots: scene.shots.map(shot =>
                    shot.id === shotId
                      ? {
                          ...shot,
                          ...(videoLink
                            ? { vidSrc: videoLink, errorGeneratingVideo: false }
                            : { errorGeneratingVideo: true })
                        }
                      : shot
                  )
                }
              }
              return scene
            })
          )

          if (videoLink) {
            message.success(
              `Video generated for Scene ${sceneId}, Shot ${shotId} successfully!`
            )
          }
        } catch (error) {
          console.error(
            `Error generating video for scene ${scene.id}, shot ${shot.id}:`,
            error
          )
          message.error(
            `Error generating video for Scene ${scene.id}, Shot ${shot.id}: ${error.message}`
          )
        } finally {
          // Remove from generating status when this specific request completes
          setVideoGenerating(prevStatus =>
            (prevStatus || []).filter(
              status =>
                !(status.sceneId === scene.id && status.shotId === shot.id)
            )
          )
        }
      })

      message.success('All video generation requests started.')
    } catch (error) {
      console.error('Error starting video generation:', error)
      message.error('Error starting video generation: ' + error.message)
    } finally {
      setGeneratingVideoForAllScenes(false)
    }
  }

  const handleStopMachine = async () => {
    try {
      await changeMachineState(0) // Set to 0 to stop
      message.success('Machine state changed to stopped.')
    } catch (error) {
      message.error('Error stopping machine: ' + error.message)
    }
  }

  const handleStartMachine = async () => {
    while (true) {
      try {
        await changeMachineState(1) // Attempt to start the machine
        message.success('Machine state changed to generating.')
        break // Exit the loop if successful
      } catch (error) {
        console.error('Retrying after error:', error.message)
        await new Promise(resolve => setTimeout(resolve, 1000)) // wait 1 second before retrying
      }
    }
  }

  const isAllScenesGenerated = scenes.every(scene =>
    scene.shots.every(shot => shot.vidSrc)
  )

  const handleAllRegeneration = () => {
    Modal.confirm({
      title: 'Do you want to generate all videos?',
      content: 'This will overwrite the current videos.',
      centered: true,
      onOk() {
        handleGenerateFullVideo()
      },
      onCancel() {
        console.log('Cancel')
      }
    })
  }

  const { totalVideosGenerated, totalVideosToGenerate } = useMemo(() => {
    const totalVideosGenerated = scenes.reduce((count, scene) => {
      return count + scene.shots.filter(shot => shot.vidSrc).length
    }, 0)
    const totalVideosToGenerate = scenes.reduce((count, scene) => {
      return count + scene.shots.length
    }, 0)
    return {
      totalVideosGenerated,
      totalVideosToGenerate
    }
  }, [scenes])

  if (showMainVideo) {
    if (finalVideoError || !(formData && formData.finalVideo)) {
      return (
        <Modal
          centered
          visible={showMainVideo}
          closable={true}
          onCancel={() => setShowMainVideo(false)}
          footer={[
            <Button key="close" onClick={() => setShowMainVideo(false)}>
              Close
            </Button>,
            <Button
              key="retry"
              type="primary"
              onClick={() => {
                setShowMainVideo(false)
                handleGenerateFinalVideo()
              }}
            >
              Retry
            </Button>
          ]}
        >
          <div style={{ padding: '12px 4px' }}>
            <h3 style={{ marginBottom: 8 }}>Failed to generate final video</h3>
            <div style={{ color: '#ff4d4f' }}>{finalVideoError}</div>
          </div>
        </Modal>
      )
    }

    return (
      <AIVideoModal
        visible={showMainVideo}
        videoUrl={formData.finalVideo}
        title={'Your AI Generated Video'}
        onClose={() => setShowMainVideo(false)}
      />
    )
  }

  if (generateMainVideo) {
    // show modal with loading
    return (
      <Modal
        centered
        visible={generateMainVideo}
        closable={true}
        onCancel={() => setGenerateMainVideo(false)}
        footer={null}
      >
        <div
          className="loading-modal"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '40vh'
          }}
        >
          <Spin tip="Generating Final Video..." size="large" />
        </div>
      </Modal>
    )
  }

  console.log({ scenes })

  return (
    <div className="video-generation-container">
      <div className="header">
        <StepTitle
          iconType="video-camera"
          title="Video Generation"
          description="Create videos for your project"
          isLoading={scenes.some(scene =>
            scene.shots.some(shot => shot.isGenerating)
          )}
          progress={{
            current: totalVideosGenerated,
            total: totalVideosToGenerate
          }}
        />

        {formData.finalVideo && (
          <Button
            style={{ margin: '0 8px' }}
            className="final-video-button"
            icon="eye"
            onClick={() => setShowMainVideo(true)}
          >
            Watch Final Video
          </Button>
        )}

        {isAllScenesGenerated && (
          <Button
            style={{ margin: '0 8px' }}
            icon="play-circle"
            loading={generatingVideoForAllScenes}
            onClick={handleAllRegeneration}
          >
            Re-Generate All Videos
          </Button>
        )}
      </div>

      {scenes.map(scene => (
        <Scene
          key={scene.id}
          scene={scene}
          transitions={transitions}
          formData={formData}
          isGeneratingAll={generatingVideoForAllScenes}
          videoGenerating={videoGenerating}
          setScenes={setScenes}
          handleRegenerateVideo={handleRegenerateVideo}
          handleUploadVideo={handleUploadVideo}
          handleEditSettings={handleEditSettings}
          handleTransitionSelect={handleTransitionSelect}
        />
      ))}

      {/* Settings Drawer */}
      <Drawer
        title="Edit Shot Settings"
        placement="right"
        closable={true}
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
        width={400}
      >
        {selectedSettings && (
          <SettingsContent
            settings={selectedSettings}
            updateSettings={updateSettings}
            handleAdvancedSettings={handleAdvancedSettings}
            saveSettings={saveSettings}
          />
        )}
      </Drawer>

      {/* Separate Advanced Settings Drawer */}
      <Drawer
        title="Advanced Shot Settings"
        placement="right"
        closable={true}
        onClose={() => setAdvancedSettingsVisible(false)}
        visible={advancedSettingsVisible}
        width={520}
        zIndex={1001} // Higher zIndex to appear above the settings drawer
      >
        {advancedSettings && (
          <AdvancedSettingsContent
            settings={advancedSettings}
            updateSettings={updateAdvancedSettings}
            saveSettings={saveAdvancedSettings}
            handleUploadImage={handleUploadImage}
            formData={formData}
            onCancel={() => setAdvancedSettingsVisible(false)}
          />
        )}
      </Drawer>
    </div>
  )
})

export default VideoGeneration
